import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import {
  Plus, Search, FileText, X, Upload, Eye, Edit2,
  Download, Trash2, Camera, PenLine, RotateCcw, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token') || null;


const STATUS       = ['offen','in_bearbeitung','erledigt'];
const S_LABEL      = { offen:'Offen', in_bearbeitung:'In Bearbeitung', erledigt:'Erledigt' };
const S_BADGE      = { offen:'badge-red', in_bearbeitung:'badge-yellow', erledigt:'badge-green' };
const S_DOT        = { offen:'var(--red)', in_bearbeitung:'var(--yellow)', erledigt:'var(--green)' };
const DAMAGE_TYPES = ['Unfall','Vandalismus','Hagel','Glasbruch','Motorschaden','Reifenschaden','Wasserschaden','Sonstiges'];
const YEARS        = Array.from({length:5},(_,i) => new Date().getFullYear()-i);

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';
const imgUrl = p => !p ? null : p.startsWith('http') ? p : `${window.location.origin}/uploads/damage/${p}`;

// Bild komprimieren
async function compress(file, maxPx=1400, q=0.82) {
  if (!file.type.startsWith('image/')) return file;
  return new Promise(res => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w <= maxPx && h <= maxPx) { res(file); return; }
      if (w > h) { h = Math.round(h * maxPx / w); w = maxPx; }
      else       { w = Math.round(w * maxPx / h); h = maxPx; }
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      c.toBlob(blob => res(new File([blob], file.name.replace(/\.[^.]+$/,'.jpg'), {type:'image/jpeg'})), 'image/jpeg', q);
    };
    img.onerror = () => { URL.revokeObjectURL(url); res(file); };
    img.src = url;
  });
}

// ── Unterschrift-Canvas ───────────────────────────────────────────────────────
function SignaturePad({ onSave, onClear, hasSignature }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = canvas.offsetWidth  * window.devicePixelRatio;
    canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    drawing.current = false;
    // Bild speichern
    const canvas = canvasRef.current;
    canvas.toBlob(blob => {
      if (blob) onSave(new File([blob], 'unterschrift-kunde.png', { type: 'image/png' }));
    }, 'image/png');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    onClear();
  };

  return (
    <div>
      <div style={{
        border: '2px dashed var(--border)', borderRadius: 10,
        background: '#fff', position: 'relative', overflow: 'hidden',
        touchAction: 'none',
      }}>
        <canvas
          ref={canvasRef}
          style={{ display:'block', width:'100%', height:140, cursor:'crosshair', touchAction:'none' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
        />
        {!hasSignature && (
          <div style={{
            position:'absolute', inset:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            pointerEvents:'none', color:'#94a3b8', fontSize:'.82rem', fontStyle:'italic',
          }}>
            Hier unterschreiben ✍️
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={clear} style={{ gap:5 }}>
          <RotateCcw size={12}/> Löschen
        </button>
        {hasSignature && (
          <span style={{ fontSize:'.75rem', color:'var(--green)', display:'flex', alignItems:'center', gap:4 }}>
            <Check size={13}/> Unterschrift vorhanden
          </span>
        )}
      </div>
    </div>
  );
}

const EMPTY = {
  vehicle_id:'', title:'', description:'',
  damage_date: new Date().toISOString().split('T')[0],
  location:'', damage_type:'Sonstiges', assigned_to:'',
};

export default function Schaden() {
  const loc = useLocation();
  const [reports, setReports]   = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatus] = useState(new URLSearchParams(loc.search).get('status') || '');
  const [yearFilter, setYear]   = useState('');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [lightbox, setLightbox]  = useState(null); // { images, idx }
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [images, setImages]     = useState([]);    // File[] — neue Bilder
  const [previews, setPreviews] = useState([]);    // string[] — DataURLs
  const [sigKunde, setSigKunde]         = useState(null); // File Kunde
  const [hasSigKunde, setHasSigKunde]   = useState(false);
  const [sigMitarbeiter, setSigMitarbeiter] = useState(null); // File Mitarbeiter
  const [hasSigMit, setHasSigMit]       = useState(false);
  // Datum + Name für Unterschriften
  const [sigKundeName,  setSigKundeName]  = useState('');
  const [sigKundeDatum, setSigKundeDatum] = useState('');
  const [sigMitName,    setSigMitName]    = useState('');
  const [sigMitDatum,   setSigMitDatum]   = useState('');
  const [saving, setSaving]     = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/damage', { params: { status: statusFilter, year: yearFilter || undefined } })
      .then(r => {
        let d = Array.isArray(r.data) ? r.data : [];
        if (search) d = d.filter(x =>
          x.title?.toLowerCase().includes(search.toLowerCase()) ||
          x.license_plate?.toLowerCase().includes(search.toLowerCase())
        );
        setReports(d);
      })
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [statusFilter, yearFilter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/vehicles').then(r => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditing(null); setForm(EMPTY);
    setImages([]); setPreviews([]);
    setSigKunde(null); setHasSigKunde(false);
    setSigMitarbeiter(null); setHasSigMit(false);
    setSigKundeName(''); setSigKundeDatum('');
    setSigMitName(''); setSigMitDatum('');
    setModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      vehicle_id:  r.vehicle_id  || '',
      title:       r.title       || '',
      description: r.description || '',
      damage_date: r.damage_date?.split('T')[0] || '',
      location:    r.location    || '',
      damage_type: r.damage_type || 'Sonstiges',
      assigned_to: r.assigned_to || '',
      status:      r.status,
      repair_cost: r.repair_cost || '',
      repair_notes:r.repair_notes|| '',
    });
    setImages([]); setPreviews([]);
    setSigKunde(null); setHasSigKunde(false);
    setSigMitarbeiter(null); setHasSigMit(false);
    setSigKundeName(''); setSigKundeDatum('');
    setSigMitName(''); setSigMitDatum('');
    setModal(true);
  };

  // Bilder auswählen und Vorschauen erstellen
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // Komprimieren
    const compressed = await Promise.all(files.map(f => compress(f)));

    // Vorschauen
    const newPreviews = await Promise.all(
      compressed.map(file => new Promise(res => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.readAsDataURL(file);
      }))
    );

    setImages(prev => [...prev, ...compressed]);
    setPreviews(prev => [...prev, ...newPreviews]);
    toast.success(`${files.length} Bild${files.length > 1 ? 'er' : ''} hinzugefügt`);
  };

  const removeImage = (idx) => {
    setImages(prev  => prev.filter((_,i) => i !== idx));
    setPreviews(prev => prev.filter((_,i) => i !== idx));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Bitte Fahrzeug auswählen'); return; }
    if (!form.title.trim()) { toast.error('Bitte Titel eingeben'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      // Alle Textfelder
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      // Bilder (mehrere)
      for (const img of images) {
        fd.append('images', img);
      }
      // Datum + Name für Unterschriften in Formular speichern
      if (sigKundeName)  fd.append('sig_kunde_name',  sigKundeName);
      if (sigKundeDatum) fd.append('sig_kunde_datum', sigKundeDatum);
      if (sigMitName)    fd.append('sig_mit_name',    sigMitName);
      if (sigMitDatum)   fd.append('sig_mit_datum',   sigMitDatum);

      // Unterschriften als Bilder anhängen
      if (sigKunde) {
        fd.append('images', new File([sigKunde], 'unterschrift-kunde.png', { type: 'image/png' }));
      }
      if (sigMitarbeiter) {
        fd.append('images', new File([sigMitarbeiter], 'unterschrift-mitarbeiter.png', { type: 'image/png' }));
      }

      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (editing) {
        await api.put(`/damage/${editing.id}`, fd, cfg);
        toast.success('Aktualisiert');
      } else {
        await api.post('/damage', fd, cfg);
        toast.success('Schadensmeldung erstellt');
      }
      setModal(false);
      load();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Bild löschen aus bestehender Meldung
  const deleteExistingImage = async (reportId, imageId) => {
    if (!window.confirm('Bild löschen?')) return;
    try {
      await api.delete(`/damage/${reportId}/images/${imageId}`);
      toast.success('Bild gelöscht');
      // View-Modal aktualisieren
      if (viewModal) {
        const updated = await api.get(`/damage/${viewModal.id}`);
        setViewModal(updated.data);
      }
      load();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const del = async (id) => {
    if (!window.confirm('Schadensmeldung wirklich löschen?')) return;
    try { await api.delete(`/damage/${id}`); toast.success('Gelöscht'); load(); }
    catch(err) { toast.error(err.response?.data?.error || 'Fehler'); }
  };

  const openPdf = (id) => {
    const token = getToken();
    window.open(`/api/damage/${id}/pdf?token=${token}`, '_blank');
  };

  // Drag & Drop
  const onDragOver = (e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent)'; };
  const onDragLeave = (e) => { e.currentTarget.style.borderColor = 'var(--border)'; };
  const onDrop = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = 'var(--border)';
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Layout title="Schadensmeldungen">

      {/* Filter */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:'1 1 160px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input className="f-input" placeholder="Suchen..." value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:28 }}/>
        </div>
        <select className="f-input" style={{ width:150 }} value={statusFilter} onChange={e=>setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          {STATUS.map(s=><option key={s} value={s}>{S_LABEL[s]}</option>)}
        </select>
        <select className="f-input" style={{ width:110 }} value={yearFilter} onChange={e=>setYear(e.target.value)}>
          <option value="">Alle Jahre</option>
          {YEARS.map(y=><option key={y}>{y}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14}/> Meldung</button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="loading-box"><div className="spinner spinner-lg"/></div>
      ) : reports.length === 0 ? (
        <div className="card">
          <div className="empty-box">
            <FileText size={40}/>
            <h3>Keine Schadensmeldungen</h3>
            <button className="btn btn-primary" style={{marginTop:14}} onClick={openCreate}><Plus size={14}/> Anlegen</button>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {reports.map(r => (
            <div key={r.id} className="card" style={{ padding:'14px 16px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:S_DOT[r.status]||'#94a3b8', flexShrink:0, marginTop:6 }}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.title}
                  </div>
                  <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:2 }}>
                    {r.brand} {r.model} · <span style={{fontFamily:'monospace',fontWeight:700}}>{r.license_plate}</span>
                    {r.damage_date && ` · ${new Date(r.damage_date).toLocaleDateString('de-DE')}`}
                  </div>
                  <div style={{ display:'flex', gap:5, marginTop:6, flexWrap:'wrap', alignItems:'center' }}>
                    <span className={`badge ${S_BADGE[r.status]||'badge-gray'}`}>{S_LABEL[r.status]}</span>
                    {r.images?.length > 0 && (
                      <span style={{ fontSize:'.68rem', color:'var(--text-muted)' }}>📷 {r.images.length} Foto{r.images.length!==1?'s':''}</span>
                    )}
                  </div>
                </div>
                <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setViewModal(r)}><Eye size={14}/></button>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>openPdf(r.id)}><Download size={14}/></button>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>openEdit(r)}><Edit2 size={14}/></button>
                  <button className="btn btn-ghost btn-icon btn-sm" style={{color:'var(--red)'}} onClick={()=>del(r.id)}><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,.95)', display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,.1)', flexShrink:0 }}>
            <span style={{ color:'#fff', fontWeight:700 }}>Foto {lightbox.idx+1}/{lightbox.images.length}</span>
            <button onClick={()=>setLightbox(null)} style={{ background:'rgba(255,255,255,.15)', border:'none', color:'#fff', borderRadius:8, padding:'7px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
              <X size={16}/> Schließen
            </button>
          </div>
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <img src={imgUrl(lightbox.images[lightbox.idx].filename)} alt="" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', borderRadius:8 }}/>
          </div>
          <div style={{ display:'flex', gap:8, padding:'12px 16px', overflowX:'auto', flexShrink:0, borderTop:'1px solid rgba(255,255,255,.1)' }}>
            {lightbox.images.map((img, i) => (
              <img key={img.id} src={imgUrl(img.filename)} alt="" onClick={()=>setLightbox(p=>({...p,idx:i}))}
                style={{ width:56, height:56, objectFit:'cover', borderRadius:6, cursor:'pointer', opacity:i===lightbox.idx?1:.5, border:i===lightbox.idx?'2px solid #fff':'none', flexShrink:0 }}/>
            ))}
          </div>
          <div style={{ position:'absolute', inset:0, zIndex:-1 }} onClick={()=>setLightbox(null)}/>
        </div>
      )}

      {/* Ansicht Modal */}
      {viewModal && !lightbox && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setViewModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-head">
              <span className="modal-head-title">#{String(viewModal.id).padStart(4,'0')} — {viewModal.title}</span>
              <div style={{ display:'flex', gap:6 }}>
                <button className="btn btn-secondary btn-sm" onClick={()=>openPdf(viewModal.id)}><Download size={13}/> PDF</button>
                <button className="btn btn-ghost btn-icon" onClick={()=>setViewModal(null)}><X size={16}/></button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:8 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'.95rem' }}>{viewModal.title}</div>
                  <div style={{ fontSize:'.78rem', color:'var(--text-muted)', marginTop:3 }}>
                    {viewModal.brand} {viewModal.model} · <span style={{fontFamily:'monospace',fontWeight:700}}>{viewModal.license_plate}</span>
                  </div>
                </div>
                <span className={`badge ${S_BADGE[viewModal.status]||'badge-gray'}`}>{S_LABEL[viewModal.status]}</span>
              </div>

              <div className="form-grid g2" style={{ marginBottom:14 }}>
                {[
                  ['Datum', viewModal.damage_date?new Date(viewModal.damage_date).toLocaleDateString('de-DE'):'-'],
                  ['Ort', viewModal.location||'-'],
                  ['Schadensart', viewModal.damage_type||'-'],
                  ['Gemeldet von', viewModal.reporter_name||'-'],
                  ['Reparaturkosten', viewModal.repair_cost ? `${parseFloat(viewModal.repair_cost).toFixed(2)} €` : '-'],
                  ['Zugewiesen', viewModal.assignee_name||'-'],
                ].map(([l,v])=>(
                  <div key={l}>
                    <div style={{ fontSize:'.65rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>{l}</div>
                    <div style={{ fontWeight:500, fontSize:'.85rem' }}>{v}</div>
                  </div>
                ))}
              </div>

              {viewModal.description && (
                <div style={{ background:'var(--bg)', borderRadius:8, padding:'12px 14px', marginBottom:14, fontSize:'.85rem', lineHeight:1.6 }}>
                  {viewModal.description}
                </div>
              )}

              {/* Bilder */}
              {viewModal.images?.length > 0 && (
                <div>
                  <div style={{ fontSize:'.68rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>
                    Fotos ({viewModal.images.length})
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:8 }}>
                    {viewModal.images.map((img, idx) => (
                      <div key={img.id} style={{ position:'relative', borderRadius:8, overflow:'hidden', aspectRatio:'4/3', background:'var(--bg)', border:'1px solid var(--border)' }}>
                        <img
                          src={imgUrl(img.filename)} alt={img.original_name}
                          style={{ width:'100%', height:'100%', objectFit:'cover', cursor:'pointer' }}
                          onClick={() => setLightbox({ images: viewModal.images, idx })}
                          onError={e=>e.target.style.display='none'}
                        />
                        <button
                          onClick={() => deleteExistingImage(viewModal.id, img.id)}
                          style={{ position:'absolute', top:4, right:4, background:'rgba(220,38,38,.85)', border:'none', color:'#fff', borderRadius:5, padding:'3px 5px', cursor:'pointer', fontSize:'.65rem' }}>
                          <Trash2 size={10}/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={()=>setViewModal(null)}>Schließen</button>
              <button className="btn btn-primary" onClick={()=>{setViewModal(null);openEdit(viewModal);}}>Bearbeiten</button>
            </div>
          </div>
        </div>
      )}

      {/* Erstellen/Bearbeiten Modal */}
      {modal && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-head">
              <span className="modal-head-title">{editing?'Schadensmeldung bearbeiten':'Neue Schadensmeldung'}</span>
              <button className="btn btn-ghost btn-icon" onClick={()=>setModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={save} style={{ display:'contents' }}>
              <div className="modal-body">
                <div className="form-grid">

                  {/* Fahrzeug */}
                  <div className="f-group">
                    <label className="f-label">Fahrzeug *</label>
                    <select className="f-input" value={form.vehicle_id} onChange={e=>f('vehicle_id',e.target.value)} required>
                      <option value="">-- Fahrzeug wählen --</option>
                      {vehicles.map(v=>(
                        <option key={v.id} value={v.id}>{v.license_plate} — {v.brand} {v.model}</option>
                      ))}
                    </select>
                  </div>

                  {/* Titel */}
                  <div className="f-group">
                    <label className="f-label">Titel *</label>
                    <input className="f-input" value={form.title} onChange={e=>f('title',e.target.value)} placeholder="Kurze Beschreibung" required/>
                  </div>

                  {/* Datum + Ort */}
                  <div className="form-grid g2">
                    <div className="f-group">
                      <label className="f-label">Schadenstag *</label>
                      <input type="date" className="f-input" value={form.damage_date} onChange={e=>f('damage_date',e.target.value)} required/>
                    </div>
                    <div className="f-group">
                      <label className="f-label">Ort</label>
                      <input className="f-input" value={form.location} onChange={e=>f('location',e.target.value)} placeholder="z.B. Parkplatz Nettetal"/>
                    </div>
                  </div>

                  {/* Schadensart */}
                  <div className="f-group">
                    <label className="f-label">Schadensart</label>
                    <select className="f-input" value={form.damage_type} onChange={e=>f('damage_type',e.target.value)}>
                      {DAMAGE_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>

                  {/* Beschreibung */}
                  <div className="f-group">
                    <label className="f-label">Beschreibung</label>
                    <textarea className="f-input" value={form.description} onChange={e=>f('description',e.target.value)} rows={3} placeholder="Genaue Beschreibung des Schadens..."/>
                  </div>

                  {/* Bei Bearbeitung: Status + Kosten */}
                  {editing && (
                    <div className="form-grid g2">
                      <div className="f-group">
                        <label className="f-label">Status</label>
                        <select className="f-input" value={form.status} onChange={e=>f('status',e.target.value)}>
                          {STATUS.map(s=><option key={s} value={s}>{S_LABEL[s]}</option>)}
                        </select>
                      </div>
                      <div className="f-group">
                        <label className="f-label">Reparaturkosten (€)</label>
                        <input type="number" step="0.01" className="f-input" value={form.repair_cost||''} onChange={e=>f('repair_cost',e.target.value)} placeholder="0.00"/>
                      </div>
                    </div>
                  )}

                  {/* ── FOTOS UPLOAD ── */}
                  <div className="f-group">
                    <label className="f-label">Fotos (mehrere möglich)</label>

                    {/* Bestehende Bilder anzeigen */}
                    {editing && editing.images?.length > 0 && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginBottom:6 }}>
                          Vorhandene Fotos ({editing.images.length}):
                        </div>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {editing.images.map(img => (
                            <div key={img.id} style={{ position:'relative', width:60, height:60, borderRadius:7, overflow:'hidden', border:'1px solid var(--border)' }}>
                              <img src={imgUrl(img.filename)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Bild löschen?')) return;
                                  try {
                                    await api.delete(`/damage/${editing.id}/images/${img.id}`);
                                    setEditing(prev => ({ ...prev, images: prev.images.filter(i=>i.id!==img.id) }));
                                    toast.success('Bild gelöscht');
                                  } catch { toast.error('Fehler'); }
                                }}
                                style={{ position:'absolute', top:2, right:2, background:'rgba(220,38,38,.9)', border:'none', color:'#fff', borderRadius:4, padding:'2px 4px', cursor:'pointer' }}>
                                <X size={9}/>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Neue Bilder Vorschau */}
                    {previews.length > 0 && (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                        {previews.map((src, i) => (
                          <div key={i} style={{ position:'relative', width:60, height:60, borderRadius:7, overflow:'hidden', border:'2px solid var(--green)', background:'var(--bg)' }}>
                            <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,.7)', border:'none', color:'#fff', borderRadius:4, padding:'2px 4px', cursor:'pointer' }}>
                              <X size={9}/>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Zone */}
                    <div
                      className="upload-zone"
                      style={{ padding:'16px 12px' }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}>
                      <Camera size={22} style={{ marginBottom:6 }}/>
                      <div style={{ fontWeight:600, fontSize:'.85rem' }}>
                        Fotos hinzufügen
                      </div>
                      <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:3 }}>
                        Tippen zum Auswählen · Drag & Drop · Mehrere möglich
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        capture="environment"
                        style={{ display:'none' }}
                        onChange={e => handleFiles(e.target.files)}
                      />
                    </div>
                    {images.length > 0 && (
                      <div style={{ fontSize:'.75rem', color:'var(--green)', marginTop:5, fontWeight:600 }}>
                        ✓ {images.length} neue{images.length===1?'s':''} Foto{images.length===1?'':'s'} bereit
                      </div>
                    )}
                  </div>

                  {/* ── UNTERSCHRIFTEN ── */}
                  <div className="f-group">
                    <label className="f-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <PenLine size={13}/> Unterschriften
                    </label>
                    <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginBottom:10 }}>
                      Beide Parteien können hier direkt auf dem Gerät unterschreiben. Die Unterschriften erscheinen ordentlich im PDF.
                    </div>

                    {/* Zwei Felder nebeneinander auf Desktop, untereinander auf Mobile */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="fg-sig-grid">

                      {/* Kunde */}
                      <div>
                        <div style={{ fontSize:'.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
                          Kunde
                        </div>
                        <SignaturePad
                          hasSignature={hasSigKunde}
                          onSave={blob => { setSigKunde(blob); setHasSigKunde(true); }}
                          onClear={() => { setSigKunde(null); setHasSigKunde(false); }}
                        />
                      </div>

                      {/* Mitarbeiter */}
                      <div>
                        <div style={{ fontSize:'.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
                          Mitarbeiter
                        </div>
                        <SignaturePad
                          hasSignature={hasSigMit}
                          onSave={blob => { setSigMitarbeiter(blob); setHasSigMit(true); }}
                          onClear={() => { setSigMitarbeiter(null); setHasSigMit(false); }}
                        />
                      </div>
                    </div>

                    <style>{'.fg-sig-grid { } @media (max-width: 500px) { .fg-sig-grid { grid-template-columns: 1fr !important; } }'}</style>

                    {/* Datum + Name für beide */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:4}} className="fg-sig-grid">
                      {/* Kunde: Datum + Name */}
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        <div className="f-group">
                          <label className="f-label">Name Kunde (Druckbuchstaben)</label>
                          <input
                            className="f-input"
                            value={sigKundeName}
                            onChange={e=>setSigKundeName(e.target.value)}
                            placeholder="MAX MUSTERMANN"
                            style={{textTransform:'uppercase'}}
                          />
                        </div>
                        <div className="f-group">
                          <label className="f-label">Datum Kunde</label>
                          <input
                            type="date"
                            className="f-input"
                            value={sigKundeDatum}
                            onChange={e=>setSigKundeDatum(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* Mitarbeiter: Datum + Name */}
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        <div className="f-group">
                          <label className="f-label">Name Mitarbeiter (Druckbuchstaben)</label>
                          <input
                            className="f-input"
                            value={sigMitName}
                            onChange={e=>setSigMitName(e.target.value)}
                            placeholder="MAX MUSTERMANN"
                            style={{textTransform:'uppercase'}}
                          />
                        </div>
                        <div className="f-group">
                          <label className="f-label">Datum Mitarbeiter</label>
                          <input
                            type="date"
                            className="f-input"
                            value={sigMitDatum}
                            onChange={e=>setSigMitDatum(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-secondary" onClick={()=>setModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving && <span className="spinner" style={{width:14,height:14,borderWidth:2}}/>}
                  {editing ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
